import React from "react";
import {
  IconExternalLink,
  IconFileDownload,
  IconLayoutSidebarRightCollapse,
} from "@tabler/icons-react";
import clsx from "clsx";
import Tooltip from "components/Tooltip";
import Button from "components/Button";
import { useTranslation } from "react-i18next";
import GeneralInfo from "./GeneralInfo";
import ExternalLinks from "./ExternalLinks";
import Conformity from "./Conformity";
import ReferenceValues from "./ReferenceValues";
import EnvironmentalIndicators from "./EnvironmentalIndicators";
import { Material2050SearchOUT, Materials2050EPDDetailOUT } from "api-client";
import { epdSearchStore } from "store/Mapping/EPDSearchStore";
import LoadingView from "../LoadingView";
import EmptyView from "../EmptyView";
import { observer } from "mobx-react-lite";
import { LoadingState } from "../SearchTable";
import Alert from "components/Alert";
import { buildingProductStore } from "store/Mapping/BuildingProductStore";
import useToast from "hooks/useToast";
import ConfirmModal from "components/ConfirmModal";

const EPDOverview = (props: {
  epd: Material2050SearchOUT | null;
  close: () => void;
  handleSelectEPD: (epd_id: string) => void;
  loadingEPDSelect: LoadingState;
}) => {
  const { t } = useTranslation();
  const scrollAreaRef = React.useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = React.useState(false);
  const [details, setDetails] =
    React.useState<Materials2050EPDDetailOUT | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<number>();
  const toast = useToast();
  const [openRequestEPDAlert, setOpenRequestEPDAlert] = React.useState(false);

  React.useEffect(() => {
    getEPDDetails();
    scrollAreaRef.current?.scrollTo(0, 0);
  }, [props.epd]);

  async function getEPDDetails() {
    if (!props.epd?.id) return;
    setLoading(true);
    try {
      const response = await epdSearchStore.fetch2050EPDDetails(props.epd?.id);
      if (!response) return;
      setDetails(response);
      setError(undefined);
    } catch (error) {
      if (error && typeof error === "object" && "status" in error) {
        setError((error as { status: number }).status);
      }
    } finally {
      setLoading(false);
    }
  }

  const onScrollDiv = () => {
    setIsSticky(scrollAreaRef.current?.scrollTop !== 0);
  };

  function closeOverviewButton() {
    return (
      <Button
        leadingIcon={<IconLayoutSidebarRightCollapse />}
        width="fit-content"
        type="gray"
        className="mt-8"
        onClick={props.close}
      >
        {t("productEditor.closeOverview")}
      </Button>
    );
  }

  function onSelectEPD() {
    if (!props.epd) return;
    details?.is_good_quality
      ? props?.epd && props.handleSelectEPD(props.epd.id)
      : handleRequestEPD(details);
  }

  async function handleRequestEPD(details: Materials2050EPDDetailOUT | null) {
    if (!details?.external_links?.epd_source) return;
    await buildingProductStore
      .submitCustomEPDRequest(details?.external_links?.epd_source)
      .then(() => {
        setOpenRequestEPDAlert(true);
      })
      .catch((error) =>
        toast(
          t("productEditor.requestEPDFaild", { error_code: error.status }),
          "warning"
        )
      );
  }

  function headerTile() {
    return (
      <div
        className={clsx(
          "py-3 px-6 flex items-center justify-between sticky top-0 z-10 bg-white",
          { "shadow-md": isSticky }
        )}
      >
        <div className="flex items-center">
          <IconLayoutSidebarRightCollapse
            size={30}
            className="text-indigo-700 cursor-pointer mr-4 min-w-[23px]"
            onClick={props.close}
          />
          {props?.epd?.name && (
            <Tooltip
              className="font-semibold text-lg !cursor-auto text-left max-w-[320px]"
              truncate
            >
              {props.epd.name}
            </Tooltip>
          )}
        </div>
        <div className={clsx("flex items-center right-2 text-indigo-700 ml-2")}>
          {details?.external_links?.epd_source && (
            <IconFileDownload
              stroke={2}
              className="ml-2 cursor-pointer"
              onClick={() =>
                window.open(
                  String(details.external_links?.epd_source),
                  "_blank"
                )
              }
            />
          )}
          {details?.external_links?.materials_2050_source && (
            <IconExternalLink
              stroke={2}
              className="ml-2 cursor-pointer"
              onClick={() =>
                window.open(
                  String(details.external_links?.materials_2050_source),
                  "_blank"
                )
              }
            />
          )}
          <Button
            width="fit-content"
            type="primary"
            className="ml-3"
            size="default"
            onClick={onSelectEPD}
            loading={
              props.loadingEPDSelect.state &&
              props.loadingEPDSelect.id === props.epd?.id
            }
            disable={props.loadingEPDSelect.state}
          >
            {details?.is_good_quality
              ? t("commons.select")
              : t("commons.request")}
          </Button>
        </div>
      </div>
    );
  }

  if (props.epd === null) return null;
  if (error)
    return (
      <EmptyView
        errorCode={error}
        message={t("productEditor.overviewErrorMessage")}
        button={closeOverviewButton()}
      />
    );
  if (loading)
    return (
      <LoadingView
        message={t("productEditor.EPDInformationLoading")}
        className="!w-20 !h-20"
        button={closeOverviewButton()}
      />
    );

  return (
    <div
      className={clsx(
        "col-span-1 max-h-[calc(100vh-238px)] h-[calc(100vh-238px)] overflow-y-auto"
      )}
      ref={scrollAreaRef}
      onScroll={onScrollDiv}
    >
      {headerTile()}
      <div className="py-3 px-6">
        {!details?.is_good_quality && (
          <Alert
            type="attention"
            title={t("productEditor.lowQualityEPDTitle")}
            description={t("productEditor.lowQualityEPDMessage")}
            className="mb-6"
          />
        )}
        <GeneralInfo details={details} />
        <ExternalLinks details={details} />
        <Conformity details={details} />
        <ReferenceValues details={details} />
        <EnvironmentalIndicators details={details} />
      </div>
      <ConfirmModal
        type="success"
        open={openRequestEPDAlert}
        close={() => setOpenRequestEPDAlert(false)}
        title={t("productEditor.epdAskedSuccessfully")}
        descriptionMesage={t("productEditor.epdAskedSuccessfullyMessage")}
        onConfirm={() => setOpenRequestEPDAlert(false)}
        confirmButtonTitle={t("productEditor.understood")}
        hideCancel
      />
    </div>
  );
};

export default observer(EPDOverview);
